// CoordinateField — the site-pin input, paste-first (COMPONENTS §Catalog): ONE
// smart text field that accepts a pasted maps link (Google / OSM / geo:) or a
// bare "lat, long" pair and owns the parsing (lib/coordinates.ts), emitting the
// canonical `{ latitude, longitude } | null` — the PhoneField doctrine applied
// to coordinates (the field owns normalization; the schema validates range
// only, never re-parses). Hand-rolled (native-owned, ADR-028) over `Input`.
//
// Feedback contract: a recognizably-parsed paste shows the canonical pair as a
// quiet confirmation line; text that parses to NOTHING shows the caller's
// `invalidLabel` hint live (the value is already null — a required field's
// schema error still lands on submit, this line just answers "why didn't it
// take?" at type time). Blur normalizes the text to the canonical form.
//
// A technical value (ADR-030): LTR + Latin digits in both locales. The map-pin
// picker upgrade rides the monitoring map-engine decision — same stored value,
// richer capture; this field is the air-gap-safe v1 (no tiles, no geocoding).

import { useId, useState, type FocusEvent, type ReactNode } from 'react';
import { Input } from './Input.js';
import { Label } from './Label.js';
import { icons } from './icons.js';
import {
  formatCoordinates,
  parseCoordinates,
  sameCoordinates,
  type Coordinates,
} from '../../lib/coordinates.js';

export type { Coordinates } from '../../lib/coordinates.js';

export interface CoordinateFieldProps {
  value: Coordinates | null;
  onChange: (value: Coordinates | null) => void;
  /** Visible field label (i18n'd by the caller). ReactNode so a caller can append
   *  an `aria-hidden` marker (e.g. the activation dot). */
  label: ReactNode;
  /** Under-field affordance copy ("Paste a maps link or lat, long"). */
  hint?: string;
  /** Confirmation prefix once a paste parses ("Parsed"). Omit = pair only. */
  parsedLabel?: string;
  /** Live hint when the text parses to nothing ("No coordinates recognized"). */
  invalidLabel?: string;
  /** Validation error from the form (range / required) — wins over hints. */
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
}

export function CoordinateField({
  value,
  onChange,
  label,
  hint,
  parsedLabel,
  invalidLabel,
  error,
  required,
  disabled,
  placeholder,
  id: idProp,
}: CoordinateFieldProps) {
  const reactId = useId();
  const id = idProp ?? reactId;
  const describedBy = `${id}-feedback`;

  const [text, setText] = useState(value ? formatCoordinates(value) : '');
  const [parsed, setParsed] = useState<Coordinates | null>(value);

  // Sync an EXTERNAL value change (form reset, a loaded draft) into the text —
  // during render with a prev-value guard held in STATE (never setState-in-
  // effect, never a ref during render), skipping while the user's own edit
  // round-trips through onChange (handleText moves the guard with it).
  const [lastValue, setLastValue] = useState<Coordinates | null>(value);
  if (!sameCoordinates(lastValue, value)) {
    setLastValue(value);
    if (!sameCoordinates(parsed, value)) {
      setParsed(value);
      setText(value ? formatCoordinates(value) : '');
    }
  }

  const handleText = (next: string) => {
    setText(next);
    const result = parseCoordinates(next);
    setParsed(result);
    setLastValue(result);
    onChange(result);
  };

  const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
    if (parsed) setText(formatCoordinates(parsed));
  };

  const canonical = parsed ? formatCoordinates(parsed) : null;
  const showParsed = canonical !== null && text.trim() !== canonical;
  const showInvalid = !error && parsed === null && text.trim() !== '' && Boolean(invalidLabel);

  return (
    <div>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <Input
        id={id}
        dir="ltr"
        value={text}
        onChange={(e) => handleText(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        aria-describedby={describedBy}
        selectOnFocus={false}
      />
      <div id={describedBy}>
        {showParsed && (
          <p className="mt-1 flex items-center gap-1 text-sm text-[var(--color-success)]" dir="ltr">
            <icons.check aria-hidden size={14} weight="bold" />
            {parsedLabel ? `${parsedLabel}: ${canonical}` : canonical}
          </p>
        )}
        {showInvalid && <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{invalidLabel}</p>}
        {!showParsed && !showInvalid && hint && !error && (
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{hint}</p>
        )}
      </div>
    </div>
  );
}
